'use client';

import { useState } from 'react';

interface Clip {
  order_index: number;
  question_label: string;
  url: string;
  captioned: boolean;
}

type Phase = 'form' | 'uploading' | 'processing' | 'done' | 'error';

const inputClass =
  'w-full rounded-xl border border-white/10 bg-white/[0.05] px-4 py-2.5 font-mk-body text-mk-ink placeholder:text-mk-muted/50 outline-none transition-colors focus:border-mk-pink/60';

const MAX_DURATION_SEC = 300; // 5 min — must match the worker's demo cap.

/** Read a video file's duration in the browser so we can reject > 5 min before
 *  uploading. Resolves 0 (allow) if we can't read it — the worker re-checks. */
function readDuration(file: File): Promise<number> {
  return new Promise((resolve) => {
    if (!file.type.startsWith('video/')) return resolve(0);
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.onloadedmetadata = () => {
      URL.revokeObjectURL(v.src);
      resolve(v.duration || 0);
    };
    v.onerror = () => resolve(0);
    v.src = URL.createObjectURL(file);
  });
}

export default function TrimSplitTool({ enabled = true }: { enabled?: boolean }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [question, setQuestion] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [phase, setPhase] = useState<Phase>('form');
  const [stage, setStage] = useState('Working on it');
  const [error, setError] = useState<string | null>(null);
  const [clips, setClips] = useState<Clip[]>([]);

  function fail(msg: string) {
    setError(msg);
    setPhase('error');
  }

  async function pollStatus(projectId: string) {
    try {
      const res = await fetch(`/api/public/trim-split/status?projectId=${encodeURIComponent(projectId)}`);
      const data = await res.json();
      if (data.status === 'processing') {
        setStage(data.stage || 'Working on it');
        setTimeout(() => pollStatus(projectId), 4000);
        return;
      }
      if (data.status === 'done') {
        setClips((data.clips as Clip[]) ?? []);
        setPhase('done');
        return;
      }
      fail(data.detail || 'The demo could not finish. Please try a different video.');
    } catch {
      setTimeout(() => pollStatus(projectId), 5000);
    }
  }

  async function run(e: React.FormEvent) {
    e.preventDefault();
    if (!file) return fail('Choose a video file first.');
    if (question.trim().length < 2) return fail('Enter the question or phrase that repeats in your video.');
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return fail('Enter a valid email.');
    setError(null);
    setPhase('uploading');

    // Client-side duration guard (the worker also enforces this server-side).
    const duration = await readDuration(file);
    if (duration > MAX_DURATION_SEC) {
      return fail(
        `This demo is limited to videos 5 minutes or shorter (yours is ${Math.floor(duration / 60)}m ${Math.round(
          duration % 60,
        )}s). Sign up to process full-length videos.`,
      );
    }

    // Capture the lead (best-effort, don't block on it).
    fetch('/api/public/leads', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, tool: 'trim-split' }),
    }).catch(() => {});

    try {
      // 1) presigned upload
      const presignRes = await fetch('/api/public/upload-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename: file.name,
          file_size: file.size,
          content_type: file.type || 'video/mp4',
          tool: 'trim-split',
        }),
      });
      const presign = await presignRes.json();
      if (!presignRes.ok) return fail(presign.detail || 'Upload could not start.');

      // 2) upload straight to storage
      const fd = new FormData();
      Object.entries(presign.upload.fields as Record<string, string>).forEach(([k, v]) => fd.append(k, v));
      fd.append('file', file);
      const uploadRes = await fetch(presign.upload.url, { method: 'POST', body: fd });
      if (!uploadRes.ok) return fail('Upload failed — please try a smaller file or try again.');

      // 3) start the demo
      setPhase('processing');
      setStage('Getting started');
      const startRes = await fetch('/api/public/trim-split/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: presign.key, main_question: question.trim() }),
      });
      const start = await startRes.json();
      if (!startRes.ok) return fail(start.detail || 'Could not start the demo.');

      // 4) poll
      pollStatus(start.projectId);
    } catch {
      fail('Something went wrong — please try again.');
    }
  }

  function reset() {
    setPhase('form');
    setFile(null);
    setClips([]);
    setError(null);
  }

  const busy = phase === 'uploading' || phase === 'processing';

  if (!enabled) {
    return (
      <div className="not-prose rounded-3xl border border-mk-cyan/30 bg-mk-cyan/10 px-6 py-10 text-center">
        <p className="font-mk-body text-lg text-mk-ink">This tool is coming soon.</p>
        <p className="mt-2 font-mk-body text-sm text-mk-muted">
          We&apos;re putting the finishing touches on it — check back shortly.
        </p>
      </div>
    );
  }

  return (
    <div className="not-prose">
      {(phase === 'form' || phase === 'error') && (
        <form onSubmit={run} className="space-y-4 rounded-3xl border border-white/10 bg-white/[0.04] p-6">
          <label className="block">
            <span className="mb-1.5 block font-mk-label text-xs font-bold uppercase tracking-[0.14em] text-mk-muted">
              Video file (up to 5 minutes)
            </span>
            <input
              type="file"
              accept="video/*"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="block w-full text-sm text-mk-muted file:mr-4 file:rounded-lg file:border-0 file:bg-mk-pink file:px-4 file:py-2 file:font-mk-label file:text-xs file:font-bold file:uppercase file:tracking-[0.14em] file:text-mk-on-pink hover:file:opacity-90"
            />
            {file && (
              <span className="mt-1 block text-xs text-mk-muted">
                {file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB
              </span>
            )}
          </label>

          <label className="block">
            <span className="mb-1.5 block font-mk-label text-xs font-bold uppercase tracking-[0.14em] text-mk-muted">
              The question or phrase that repeats
            </span>
            <input
              className={inputClass}
              placeholder="e.g. “What’s your biggest regret?” or a recurring quiz question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              required
            />
            <span className="mt-1 block text-xs text-mk-muted">
              Reclipt finds every spot this is asked and cuts a clip at each one.
            </span>
          </label>

          <div className="grid gap-4 sm:grid-cols-2">
            <input className={inputClass} placeholder="Your name (optional)" value={name} onChange={(e) => setName(e.target.value)} />
            <input
              className={inputClass}
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          {error && (
            <p className="rounded-xl border border-mk-pink/30 bg-mk-pink/10 px-4 py-3 font-mk-body text-sm text-mk-pink-soft">{error}</p>
          )}
          <button
            type="submit"
            className="w-full rounded-xl bg-mk-pink px-6 py-3 font-mk-label text-xs font-bold uppercase tracking-[0.14em] text-mk-on-pink transition-opacity hover:opacity-90"
          >
            Split into clips
          </button>
          <p className="text-center font-mk-body text-xs text-mk-muted">
            Free demo · up to 5 min · returns your first 3 captioned clips.
          </p>
        </form>
      )}

      {busy && (
        <div className="flex flex-col items-center gap-4 rounded-3xl border border-white/10 bg-white/[0.04] p-12 text-center">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-mk-pink/30 border-t-mk-pink" />
          <p className="font-mk-body text-mk-ink">
            {phase === 'uploading' ? 'Uploading your video…' : `${stage}…`}
          </p>
          <p className="font-mk-body text-xs text-mk-muted">
            Keep this tab open — transcribing, finding the moments, cutting, and captioning takes a couple of minutes.
          </p>
        </div>
      )}

      {phase === 'done' && (
        <div className="space-y-6">
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-4">
            <span className="font-mk-body text-sm text-mk-muted">
              {clips.length} clip{clips.length === 1 ? '' : 's'} · auto-cut + captioned by Reclipt
            </span>
          </div>

          {clips.length === 0 ? (
            <div className="rounded-3xl border border-white/10 bg-mk-s-lowest p-8 text-center font-mk-body text-mk-muted">
              We couldn&apos;t find that phrase being asked. Try a video where a question or phrase clearly repeats.
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {clips.map((clip) => (
                <div key={clip.order_index} className="overflow-hidden rounded-2xl border border-white/10 bg-mk-s-lowest">
                  <video src={clip.url} controls playsInline className="aspect-[9/16] w-full bg-black object-contain" />
                  <div className="p-3">
                    <p className="line-clamp-2 font-mk-body text-sm text-mk-ink">{clip.question_label}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-mk-pink/20 bg-mk-pink/[0.06] px-5 py-4">
            <span className="font-mk-body text-sm text-mk-ink">
              This is the free demo — 3 clips, watermarked. Reclipt does your whole library, no limits.
            </span>
            <a
              href="/register"
              className="shrink-0 rounded-xl bg-mk-pink px-5 py-2.5 font-mk-label text-xs font-bold uppercase tracking-[0.14em] text-mk-on-pink transition-opacity hover:opacity-90"
            >
              Try Reclipt →
            </a>
          </div>

          <button
            onClick={reset}
            className="rounded-xl border border-white/10 px-4 py-2 font-mk-label text-xs font-bold uppercase tracking-[0.14em] text-mk-ink transition-colors hover:border-mk-pink/50"
          >
            Try another video
          </button>
        </div>
      )}
    </div>
  );
}
